import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * モーダルを開いている間、Tab のフォーカスを中に閉じ込め、閉じたら開く前にフォーカスがあった要素へ戻すカスタムフック
 * （VI 定義 §10.4。SCR-P-001 §7.2）
 *
 * 戻す先は、開いたときに押されたボタン（opener）。macOS の Safari ではボタンを押してもフォーカスが移らないことがあり、
 * 開いた時点の document.activeElement では body を覚えてしまうため（SCR-P-001 §7.2「押されたボタンそのもの」）。
 * opener が渡されないときだけ、開いた時点の document.activeElement を使う。
 * その場合に備え、モーダルの中の要素にフォーカスを置く処理より前に呼ぶ。
 *
 * @param {React.RefObject<HTMLElement>} containerRef - フォーカスを閉じ込める要素
 * @param {HTMLElement|null} [openerElement] - 開いたボタン（クリックの e.currentTarget）
 *
 * @example
 * function MyModal({ opener, onClose }) {
 *   const dialogRef = useRef(null);
 *   useFocusTrap(dialogRef, opener);
 *   return <div role="dialog" ref={dialogRef}>...</div>;
 * }
 */
function useFocusTrap(containerRef, openerElement) {
    useEffect(() => {
        const opener = openerElement || document.activeElement;

        const handleKey = (e) => {
            const container = containerRef.current;
            if (e.key !== 'Tab' || !container) return;
            const items = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const inside = container.contains(document.activeElement);
            if (e.shiftKey && (!inside || document.activeElement === first)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('keydown', handleKey);
            // 開いたボタンが消えている（一覧が描き直された等）ときは戻さない
            if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
        };
    }, [containerRef, openerElement]);
}

export default useFocusTrap;
